import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { getAuthHeaders } from '../../api/auth';
import './HoflistePage.css';
import logoKirchhoff from './Logo Kirchhoff.png';

interface Visit {
  _id: string;
  licensePlate: string;
  driver_firstName?: string;
  driver_lastName?: string;
  driver_mobile?: string;
  carrier?: string;
  supplier?: string;
  purpose?: string;
  status: 'WAITING' | 'CALLED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  gate_code?: string;
  gate_name?: string;
  area_slug?: string;
  area_name?: string;
  checkedInAt?: string;
  calledAt?: string;
  arrivedInsidePlant?: string;
  completedAt?: string;
  completedPurposes?: string[];
  notes?: string;
  display_isVisible?: boolean;
  display_queuePosition?: number;
}

type AreaTab = 'versand' | 'wareneingang' | 'leergut' | 'historie';

const GATE_OPTIONS = ['P1', 'P2', 'P3', 'P4', 'P5A', 'P5B', 'P6', 'P7', 'P8', 'P9'];

const AREA_TABS = {
  versand: 'Versand',
  wareneingang: 'Wareneingang',
  leergut: 'Leergut',
  historie: 'Historie',
};

const STATUS_LABELS = {
  WAITING: 'Wartend',
  CALLED: 'Aufgerufen',
  COMPLETED: 'Abgeschlossen',
  CANCELLED: 'Storniert',
  NO_SHOW: 'Nicht erschienen',
};

type SortField = 'licensePlate' | 'driver_firstName' | 'carrier' | 'supplier' | 'status' | 'gate_code' | 'checkedInAt' | 'calledAt' | 'completedAt' | null;
type SortDirection = 'asc' | 'desc';

export function HoflistePage() {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AreaTab>('versand');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('active'); // 'active', 'all', 'completed'
  const [categoryFilter, setCategoryFilter] = useState<string>('all'); // 'all', 'versand', 'wareneingang', 'leergut'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingCell, setEditingCell] = useState<{ id: string; field: 'gate_code' | 'notes' } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sortField, setSortField] = useState<SortField>('checkedInAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Hilfsfunktion: purpose String zu Array (kommagetrennt)
  const parsePurpose = (purpose?: string): string[] => {
    if (!purpose) return [];
    return purpose.split(',').map(p => p.trim()).filter(p => p.length > 0);
  };

  // Fetch visits from API
  const fetchVisits = async () => {
    try {
      // tenantId wird automatisch aus JWT token extrahiert
      const response = await fetch(`${API_BASE_URL}/api/visits`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Besucher');
      }

      const data = await response.json();
      setVisits(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching visits:', err);
      setError('Fehler beim Laden der Besucher');
    } finally {
      setLoading(false);
    }
  };

  // Call visit (WAITING → CALLED)
  const handleCall = async (visitId: string) => {
    // Validierung: Tor muss ausgewählt sein
    const visit = visits.find(v => v._id === visitId);
    if (!visit?.gate_code || visit.gate_code.trim() === '') {
      alert('Bitte wählen Sie zuerst ein Tor aus, bevor Sie den Fahrer aufrufen.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/visits/${visitId}/call`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Fehler beim Aufrufen');
      }

      await fetchVisits();
    } catch (err) {
      console.error('Error calling visit:', err);
      alert('Fehler beim Aufrufen des Fahrers');
    }
  };

  // Mark visit as arrived (CALLED + set arrivedAt)
  const handleArrive = async (visitId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/visits/${visitId}/arrive`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Fehler beim Bestätigen der Ankunft');
      }

      await fetchVisits();
    } catch (err) {
      console.error('Error marking visit as arrived:', err);
      alert('Fehler beim Bestätigen der Ankunft');
    }
  };

  // Complete visit for current area (CALLED → COMPLETED or partial completion)
  const handleComplete = async (visitId: string, purpose: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/visits/${visitId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ purpose })
      });

      if (!response.ok) {
        throw new Error('Fehler beim Abschließen');
      }

      await fetchVisits();
    } catch (err) {
      console.error('Error completing visit:', err);
      alert('Fehler beim Abschließen des Besuchs');
    }
  };

  // Update visit field (gate_code or notes)
  const handleUpdateField = async (visitId: string, field: 'gate_code' | 'notes', value: string) => {
    try {
      // Don't send empty strings - skip update
      if (!value || value.trim() === '') {
        setEditingCell(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/visits/${visitId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          [field]: value,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update error:', response.status, errorText);
        throw new Error(`Fehler beim Speichern: ${response.status}`);
      }

      await fetchVisits();
      setEditingCell(null);
    } catch (err) {
      console.error('Error updating visit:', err);
      alert('Fehler beim Speichern');
    }
  };

  // Handle gate selection change immediately
  const handleGateChange = async (visitId: string, gateCode: string) => {
    if (gateCode && gateCode !== '') {
      await handleUpdateField(visitId, 'gate_code', gateCode);
    }
  };

  // Start editing cell
  const startEdit = (visitId: string, field: 'gate_code' | 'notes', currentValue: string) => {
    setEditingCell({ id: visitId, field });
    setEditValue(currentValue || '');
  };

  // Save edited value
  const saveEdit = () => {
    if (editingCell) {
      handleUpdateField(editingCell.id, editingCell.field, editValue);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Initial load
  useEffect(() => {
    fetchVisits();
  }, []);

  // Auto-refresh every 60 seconds (1 minute)
  useEffect(() => {
    const interval = setInterval(fetchVisits, 60000);
    return () => clearInterval(interval);
  }, []);

  // Filter visits by tab and search
  const filteredVisits = visits.filter((visit) => {
    // Tab filter (area_slug or purpose)
    let tabMatch = false;
    const purposes = parsePurpose(visit.purpose);
    const completedPurposes = visit.completedPurposes || [];

    if (activeTab === 'historie') {
      // Historie zeigt alle abgeschlossenen Fahrten
      tabMatch = visit.status === 'COMPLETED';

      // Zusätzlich nach Kategorie filtern im Historie-Tab
      if (categoryFilter !== 'all') {
        if (categoryFilter === 'versand') {
          tabMatch = tabMatch && purposes.some(p => p.toLowerCase() === 'versand');
        } else if (categoryFilter === 'wareneingang') {
          tabMatch = tabMatch && purposes.some(p => {
            const pLower = p.toLowerCase();
            return pLower === 'wareneingang' || pLower === 'empfang';
          });
        } else if (categoryFilter === 'leergut') {
          tabMatch = tabMatch && purposes.some(p => {
            const pLower = p.toLowerCase();
            return pLower === 'leergut' || pLower === 'sonstiges';
          });
        }
      }
    } else if (activeTab === 'versand') {
      // Versand: nur anzeigen wenn purpose 'versand' hat UND noch nicht abgeschlossen
      tabMatch = purposes.some(p => p.toLowerCase() === 'versand') &&
        !completedPurposes.includes('versand');
    } else if (activeTab === 'wareneingang') {
      // Wareneingang: nur anzeigen wenn purpose hat UND noch nicht abgeschlossen
      tabMatch = purposes.some(p => {
        const pLower = p.toLowerCase();
        return pLower === 'wareneingang' || pLower === 'empfang';
      }) && !completedPurposes.includes('wareneingang');
    } else {
      // Leergut: nur anzeigen wenn purpose hat UND noch nicht abgeschlossen
      tabMatch = purposes.some(p => {
        const pLower = p.toLowerCase();
        return pLower === 'leergut' || pLower === 'sonstiges';
      }) && !completedPurposes.some(cp => cp.toLowerCase() === 'leergut' || cp.toLowerCase() === 'sonstiges');
    }

    // Status filter (nur für nicht-Historie Tabs)
    const statusMatch = activeTab === 'historie'
      ? true // Im Historie-Tab wird Status-Filter ignoriert
      : statusFilter === 'active'
        ? visit.status === 'WAITING' || visit.status === 'CALLED'
        : statusFilter === 'completed'
          ? visit.status === 'COMPLETED'
          : true; // 'all'

    // Search filter
    const searchMatch = searchTerm
      ? visit.licensePlate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.driver_firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.driver_lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.carrier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.supplier?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    // Date range filter on checkedInAt
    const dateMatch = (() => {
      if (!visit.checkedInAt) return true;
      const checkedIn = new Date(visit.checkedInAt);
      const fromMatch = dateFrom ? checkedIn >= new Date(dateFrom + 'T00:00:00') : true;
      const toMatch = dateTo ? checkedIn <= new Date(dateTo + 'T23:59:59') : true;
      return fromMatch && toMatch;
    })();

    return tabMatch && statusMatch && searchMatch && dateMatch;
  });

  // Sort visits
  const sortedVisits = [...filteredVisits].sort((a, b) => {
    // First, prioritize CALLED status
    if (a.status === 'CALLED' && b.status !== 'CALLED') return -1;
    if (a.status !== 'CALLED' && b.status === 'CALLED') return 1;

    // Then apply user-selected sort
    if (!sortField) {
      return new Date(a.checkedInAt || 0).getTime() - new Date(b.checkedInAt || 0).getTime();
    }

    let compareResult = 0;
    const mult = sortDirection === 'asc' ? 1 : -1;

    switch (sortField) {
      case 'licensePlate':
        compareResult = (a.licensePlate || '').localeCompare(b.licensePlate || '');
        break;
      case 'driver_firstName':
        const nameA = `${a.driver_firstName || ''} ${a.driver_lastName || ''}`;
        const nameB = `${b.driver_firstName || ''} ${b.driver_lastName || ''}`;
        compareResult = nameA.localeCompare(nameB);
        break;
      case 'carrier':
        compareResult = (a.carrier || '').localeCompare(b.carrier || '');
        break;
      case 'supplier':
        compareResult = (a.supplier || '').localeCompare(b.supplier || '');
        break;
      case 'status':
        compareResult = (a.status || '').localeCompare(b.status || '');
        break;
      case 'gate_code':
        compareResult = (a.gate_code || '').localeCompare(b.gate_code || '');
        break;
      case 'checkedInAt':
        compareResult = new Date(a.checkedInAt || 0).getTime() - new Date(b.checkedInAt || 0).getTime();
        break;
      case 'calledAt':
        compareResult = new Date(a.calledAt || 0).getTime() - new Date(b.calledAt || 0).getTime();
        break;
      case 'completedAt':
        compareResult = new Date(a.completedAt || 0).getTime() - new Date(b.completedAt || 0).getTime();
        break;
      default:
        compareResult = 0;
    }

    return compareResult * mult;
  });

  // Count visits per tab (only active: WAITING and CALLED)
  const getTabCount = (tab: AreaTab) => {
    return visits.filter((visit) => {
      // Historie tab: count all COMPLETED visits
      if (tab === 'historie') {
        return visit.status === 'COMPLETED';
      }

      // Only count active visits for other tabs
      const isActive = visit.status === 'WAITING' || visit.status === 'CALLED';
      if (!isActive) return false;

      // Check tab match - berücksichtigt completedPurposes
      const purposes = parsePurpose(visit.purpose);
      const completedPurposes = visit.completedPurposes || [];

      if (tab === 'versand') {
        return purposes.some(p => p.toLowerCase() === 'versand') &&
          !completedPurposes.includes('versand');
      }
      if (tab === 'wareneingang') {
        return purposes.some(p => {
          const pLower = p.toLowerCase();
          return pLower === 'wareneingang' || pLower === 'empfang';
        }) && !completedPurposes.includes('wareneingang');
      }
      // Leergut
      return purposes.some(p => {
        const pLower = p.toLowerCase();
        return pLower === 'leergut' || pLower === 'sonstiges';
      }) && !completedPurposes.some(cp => cp.toLowerCase() === 'leergut' || cp.toLowerCase() === 'sonstiges');
    }).length;
  };

  // Check if waiting too long (> 60 minutes)
  const isWaitingTooLong = (visit: Visit): boolean => {
    if (visit.status !== 'WAITING' || !visit.checkedInAt) return false;

    const checkedInTime = new Date(visit.checkedInAt).getTime();
    const now = Date.now();
    const waitingMinutes = (now - checkedInTime) / (1000 * 60);

    return waitingMinutes > 60;
  };

  // Format time
  const formatTime = (isoString?: string) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="hofliste-page">
        <div className="hofliste-loading">Lade Besucher...</div>
      </div>
    );
  }

  return (
    <div className="hofliste-page">
      {/* Header */}
      <header className="hofliste-header">
        <div className="hofliste-title-group">
        </div>
      </header>

      {/* Tabs */}
      <div className="hofliste-tabs">
        {Object.entries(AREA_TABS).map(([key, label]) => (
          <button
            key={key}
            className={`tab-button ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key as AreaTab)}
          >
            {label} {key !== 'historie' && <span className="tab-count">({getTabCount(key as AreaTab)})</span>}
          </button>
        ))}
        <img src={logoKirchhoff} alt="Kirchhoff" className="hofliste-logo-tabs" />
      </div>

      {/* Filters */}
      <div className="hofliste-filters">
        <div className="filter-group filter-search">
          <input
            type="text"
            className="search-input"
            placeholder="Suche..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group filter-status">
          {activeTab === 'historie' ? (
            <select
              className="status-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">Alle Kategorien</option>
              <option value="versand">Versand</option>
              <option value="wareneingang">Wareneingang</option>
              <option value="leergut">Leergut</option>
            </select>
          ) : (
            <select
              className="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="active">Aktiv</option>
              <option value="all">Alle</option>
              <option value="completed">Abgeschlossen</option>
            </select>
          )}
        </div>
        <div className="filter-group filter-date">
          <input
            type="date"
            className="date-input"
            placeholder="Von"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="Von Datum"
          />
        </div>
        <div className="filter-group filter-date">
          <input
            type="date"
            className="date-input"
            placeholder="Bis"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="Bis Datum"
          />
        </div>
        <div className="filter-group filter-button">
          <button className="refresh-button" onClick={fetchVisits} title="Aktualisieren">
            Aktualisieren
          </button>
        </div>
        <div className="filter-stats">
          {/* Entry count removed */}
        </div>
      </div>

      {error && (
        <div className="hofliste-error">⚠️ {error}</div>
      )}

      {/* Table */}
      <div className="hofliste-content">
        {sortedVisits.length === 0 ? (
          <div className="hofliste-empty">
            <div className="empty-icon">📋</div>
            <p>Keine Besucher gefunden</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="hofliste-table">
              <thead>
                <tr>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('licensePlate')}>
                      Kennzeichen
                      {sortField === 'licensePlate' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('driver_firstName')}>
                      Fahrer
                      {sortField === 'driver_firstName' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('carrier')}>
                      Spedition
                      {sortField === 'carrier' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('supplier')}>
                      Lieferant
                      {sortField === 'supplier' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>Mobilnummer</th>
                  <th>Ziel</th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('status')}>
                      Status
                      {sortField === 'status' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('gate_code')}>
                      Tor
                      {sortField === 'gate_code' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>Notizen</th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('checkedInAt')}>
                      Check-In
                      {sortField === 'checkedInAt' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('calledAt')}>
                      Aufgerufen
                      {sortField === 'calledAt' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button className="sort-button" onClick={() => handleSort('completedAt')}>
                      Abgeschlossen
                      {sortField === 'completedAt' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th className="col-actions">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sortedVisits.map((visit) => (
                  <tr
                    key={visit._id}
                    className={`visit-row status-${visit.status.toLowerCase()}${isWaitingTooLong(visit) ? ' waiting-overdue' : ''}`}
                  >
                    <td className="col-license">
                      <strong>{visit.licensePlate}</strong>
                    </td>
                    <td>
                      {visit.driver_firstName} {visit.driver_lastName}
                    </td>
                    <td>{visit.carrier || '-'}</td>
                    <td>{visit.supplier || '-'}</td>
                    <td>{visit.driver_mobile || '-'}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>
                      {parsePurpose(visit.purpose).join('\n') || '-'}
                    </td>
                    <td>
                      <span className={`status-badge status-${visit.status.toLowerCase()}`}>
                        {STATUS_LABELS[visit.status]}
                      </span>
                    </td>
                    <td className="col-gate">
                      <select
                        className="gate-select"
                        value={visit.gate_code || ''}
                        onChange={(e) => handleGateChange(visit._id, e.target.value)}
                        disabled={visit.status === 'COMPLETED'}
                      >
                        <option value="">-- Tor wählen --</option>
                        {GATE_OPTIONS.map((gate) => (
                          <option key={gate} value={gate}>
                            {gate}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={visit.status === 'COMPLETED' ? 'col-readonly' : 'col-editable'} onClick={() => visit.status !== 'COMPLETED' && startEdit(visit._id, 'notes', visit.notes || '')}>
                      {editingCell?.id === visit._id && editingCell?.field === 'notes' ? (
                        <input
                          type="text"
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                        />
                      ) : (
                        <span className="editable-content">
                          {visit.notes || (visit.status === 'COMPLETED' ? '-' : <em>Klicken zum Bearbeiten</em>)}
                        </span>
                      )}
                    </td>
                    <td className="col-time">{formatTime(visit.checkedInAt)}</td>
                    <td className="col-time">{formatTime(visit.calledAt)}</td>
                    <td className="col-time">{formatTime(visit.completedAt)}</td>
                    <td className="col-actions">
                      <div className="action-buttons">
                        {visit.status === 'WAITING' && (
                          <button
                            className="btn-call"
                            onClick={() => handleCall(visit._id)}
                            title="Fahrer aufrufen"
                          >
                            Aufrufen
                          </button>
                        )}
                        {visit.status === 'CALLED' && !visit.arrivedInsidePlant && (
                          <button
                            className="btn-arrive"
                            onClick={() => handleArrive(visit._id)}
                            title="Fahrer ist angekommen"
                          >
                            Abruf erfolgreich
                          </button>
                        )}
                        {visit.status === 'CALLED' && visit.arrivedInsidePlant && (
                          <button
                            className="btn-complete"
                            onClick={() => {
                              // Determine which purpose to complete based on active tab
                              let purposeToComplete = '';
                              if (activeTab === 'versand') purposeToComplete = 'versand';
                              else if (activeTab === 'wareneingang') purposeToComplete = 'wareneingang';
                              else if (activeTab === 'leergut') purposeToComplete = 'leergut';
                              handleComplete(visit._id, purposeToComplete);
                            }}
                            title="Besuch abschließen"
                          >
                            Abschließen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="hofliste-footer">
        <span className="refresh-indicator">🔄 Automatische Aktualisierung alle 60 Sekunden</span>
      </div>
    </div>
  );
}


